/**
 * SybaSoil per-liter savings badge for Kaching cart drawer
 * --------------------------------------------------------
 *
 * Problem: Kaching cart auto-renders a "Save €X" badge on any line item with
 * a compare_at_price. SybaSoil multi-litre variants have compare_at_price
 * set to "5L rate × volume" (a per-liter savings indicator, NOT a real promo),
 * so Kaching shows e.g. "Save €101,82" on 6× 20L Alocasia — misleading because
 * it's not a current discount, just savings vs buying 5L bags.
 *
 * Fix: in Control (no offer module active), HIDE Kaching's auto-badge for
 * SybaSoil items and render a custom "Save X%/L" badge instead. In Variant B
 * (offer module active), skip entirely — Intelligems handles the discount UX.
 *
 * Detection of Control vs Variant B:
 *   cart.attributes._sybasoil_offer_active === 'true' = Variant B
 *   (set by snippets/sybasoil-offer-tier-cards.liquid on render)
 *
 * Baseline assumption: every SybaSoil 5L variant is €12,99 (verified via
 * Shopify API on 2026-05-14, all 15 products). If 5L base price ever
 * changes, update SYBASOIL_5L_BASELINE_CENTS below.
 *
 * No-op for: 1L variants, 5L variants (no per-liter saving vs baseline),
 * non-SybaSoil products, and any cart with the offer-active attribute.
 */

window.SybaCartPerliterBadge = window.SybaCartPerliterBadge || (function () {
  'use strict';

  // 5L baseline is read DYNAMICALLY from each product's own 5L variant price
  // (in current presentment currency) via /products/{handle}.js. This works
  // currency-agnostically for EUR/SEK/DKK and any future market the merchant
  // adds — the baseline rate is always denominated in the same units as the
  // line item's price, so the math is consistent.
  //
  // Old approach (removed): hardcoded SYBASOIL_5L_BASELINE_CENTS = 1299. That
  // worked for EUR but broke in SEK (149 SEK 5L vs assumed 12.99 EUR baseline,
  // produced negative savings → badge skipped). See PR for full rationale.

  // Locale string for badge — JS substitutes %pct% at runtime.
  // Defaults to English; theme can override via window.sybaCartBadgeStrings.
  var DEFAULT_BADGE_TEMPLATE = 'Save %pct%%/L';

  // Welcome Bundle handles (type: Care Bundles). These show "Save X%" (no /L)
  // in the KaChing cart because per-litre framing is wrong for plant+soil bundles.
  // KaChing's own native "Save €X" badge may still render alongside ours (it's a
  // 3rd-party app DOM we can't suppress from theme code) — DONE_WITH_CONCERNS noted.
  var CARE_BUNDLE_HANDLES = [
    'happy-universal-starter-bundle',
    'happy-tropical-jungle-starter-bundle',
    'happy-cacti-succulent-starter-bundle',
    'happy-orchid-starter-bundle',
    'happy-monstera-philodendron-starter-bundle'
  ];

  // NB: Shopify's storefront /products/{handle}.js returns the field as `type`,
  // NOT `product_type`. (`product_type` is the GraphQL Admin API field name.)
  // Cache for /products/{handle}.js — avoids repeated requests for same handle.
  var productCache = Object.create(null);
  var inFlightRequests = Object.create(null);

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    if (inFlightRequests[handle]) return inFlightRequests[handle];
    inFlightRequests[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { if (p) productCache[handle] = p; delete inFlightRequests[handle]; return p; })
      .catch(function () { delete inFlightRequests[handle]; return null; });
    return inFlightRequests[handle];
  }

  function parseLitres(variantTitle) {
    if (!variantTitle) return 0;
    var match = String(variantTitle).match(/(\d+)\s*L/i);
    return match ? parseInt(match[1], 10) : 0;
  }

  function getBadgeTemplate() {
    if (window.sybaCartBadgeStrings && window.sybaCartBadgeStrings.perL) {
      return window.sybaCartBadgeStrings.perL;
    }
    return DEFAULT_BADGE_TEMPLATE;
  }

  function formatBadge(pct) {
    return getBadgeTemplate().replace('%pct%', String(pct));
  }

  // Extract variant_id from the line item's title link (?variant=<id>).
  // We iterate by DOM rather than by cart.items because a single product can
  // have multiple variants in cart (each = its own .kaching-cart-item element)
  // and they all share data-product-id — so handle/product-id alone can't
  // disambiguate which DOM element corresponds to which cart line.
  function extractVariantId(domItem) {
    var link = domItem.querySelector('.kaching-cart-item__title');
    var href = link ? (link.getAttribute('href') || '') : '';
    var m = href.match(/[?&]variant=(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  }

  // Is this cart line part of an ACTIVE tier offer?
  // The SybaSoil offer module adds the soil bag with NO identifying line
  // property (assets/sybasoil-offer-tier-cards.js: { id, quantity }), so we
  // detect offer lines by their Intelligems "Nx" tier discount — IG's cart
  // transform stamps a line_level_discount_allocation whose
  // discount_application.title looks like "3x" / "6x" (tier 2 / tier 3).
  // A normal SybaSoil/Moss line added OUTSIDE the offer has no such discount.
  // Used only in Variant B to keep the per-litre badge OFF the discounted
  // offer lines (avoids double savings messaging) while still showing it on
  // non-offer SybaSoil/Moss lines. (User decision 2026-05-16.)
  function lineIsInOffer(item) {
    var allocs = item && item.line_level_discount_allocations;
    if (!allocs || !allocs.length) return false;
    for (var i = 0; i < allocs.length; i++) {
      var da = allocs[i] && allocs[i].discount_application;
      var title = da && da.title;
      if (title && /\d+\s*x/i.test(String(title))) return true;
    }
    return false;
  }

  // Mark line items + render badges. Idempotent — safe to call repeatedly.
  function renderBadges() {
    // Coalesced cart read (assets/syba-cart-state.js): shares a single
    // in-flight GET /cart.js with other syba scripts during the load burst.
    // Single-flight only — no cached/stale data (see module header).
    // Fallback keeps the badge fully functional if the module is absent.
    return (window.SybaCartState
      ? window.SybaCartState.getCart()
      : fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }))
      .then(function (cart) {
        // Variant B detection — skip rendering, let IG handle savings UX.
        var isVariantB = cart.attributes
          && (cart.attributes._sybasoil_offer_active === 'true'
              || cart.attributes._sybasoil_offer_active === true);

        // Build a variant_id → cart_item lookup for DOM iteration below.
        var byVariantId = Object.create(null);
        cart.items.forEach(function (item) {
          if (item.variant_id) byVariantId[item.variant_id] = item;
        });

        // Iterate DOM elements (one per cart line) and render per-element.
        var domItems = Array.from(document.querySelectorAll('.kaching-cart-item'));
        var promises = domItems.map(function (domItem) {
          var variantId = extractVariantId(domItem);
          if (!variantId) return Promise.resolve();
          var item = byVariantId[variantId];
          if (!item) return Promise.resolve();

          return fetchProduct(item.handle).then(function (product) {
            if (!product) return;
            // #3: keep the variant-picker Save X%/L for these two, but do
            // NOT render the badge in the KaChing cart for them.
            if (product.handle === 'sphagnum-moss-5-liter'
                || product.handle === 'sphaxx-indoor-grown-sphagnum') return;

            // #4: Welcome Bundle (Care Bundles) — render "Save X%" (no /L).
            // compare_at_price vs price from the cart item (set by Shopify on the
            // variant). KaChing's native "Save €X" badge may still show alongside
            // (3rd-party app DOM we can't suppress from theme code).
            if (CARE_BUNDLE_HANDLES.indexOf(product.handle) !== -1) {
              if (isVariantB) { removeOurBadge(domItem); return; }
              var cap = item.compare_at_price;
              var pr  = item.price;
              if (!cap || cap <= pr) { removeOurBadge(domItem); return; }
              var bundlePct = Math.round((cap - pr) / cap * 100);
              if (bundlePct <= 0) { removeOurBadge(domItem); return; }
              var savePrefix = (window.sybaCartBadgeStrings && window.sybaCartBadgeStrings.savePrefix) || 'Save';
              injectOurBadge(domItem, savePrefix + ' ' + bundlePct + '%');
              return;
            }

            // Product types that get the per-litre Save X%/L badge.
            // Houseplants Mixes = SybaSoil; Moss = Sphagnum + Sphaxx.
            if (product.type !== 'Houseplants Mixes' && product.type !== 'Moss') return;

            // Variant B: the per-litre badge would double up on the offer
            // module's own savings UX, but ONLY on lines that are actually in
            // the tier offer (IG discounts those with an "Nx" allocation).
            // Non-offer SybaSoil/Moss lines added outside the offer still get
            // the per-litre badge in B. (User decision 2026-05-16: "show in
            // B, but only on lines NOT in the offer".) Control: always show.
            if (isVariantB && lineIsInOffer(item)) {
              removeOurBadge(domItem);
              return;
            }

            var litres = parseLitres(item.variant_title);
            if (litres <= 0) {
              removeOurBadge(domItem);
              return;
            }

            // Find smallest-volume variant ≥ 5L on this product → use its
            // price as the per-litre baseline rate. The ≥5L threshold skips
            // 1L "sample/trial" variants that some SybaSoil products carry
            // (priced as loss-leaders → would wildly overstate % savings if
            // used as baseline). All real merchant-intended baselines are
            // 5L+ (SybaSoil 5L, Sphagnum 5L, Sphaxx ~10L). Currency-agnostic.
            // Must match the threshold in snippets/product-variant-options.liquid.
            var baselineVariant = null;
            var baselineVolume = Infinity;
            (product.variants || []).forEach(function (v) {
              if (!v || !v.title) return;
              var vol = parseLitres(v.title);
              if (vol >= 5 && vol < baselineVolume) {
                baselineVolume = vol;
                baselineVariant = v;
              }
            });
            if (!baselineVariant || !baselineVolume || baselineVolume === Infinity) {
              // No parseable-volume variant on this product → can't compute. Skip.
              removeOurBadge(domItem);
              return;
            }

            // Don't render a savings badge on the baseline variant itself
            // (no per-litre saving vs itself by definition).
            if (litres <= baselineVolume) {
              removeOurBadge(domItem);
              return;
            }

            // Baseline: what `litres` litres would cost at the smallest variant's per-litre rate.
            var baseline = Math.round(baselineVariant.price * litres / baselineVolume);
            var savings = baseline - item.price;
            if (savings <= 0) {
              removeOurBadge(domItem);
              return;
            }

            // Floor (not round) to match the PDP variant card's Liquid integer
            // division — keeps the percentage shown on PDP and in cart identical.
            // Sphaxx ~25L example: 19.99% → both surfaces show "19%/L".
            var pct = Math.floor((savings / baseline) * 100);
            injectOurBadge(domItem, formatBadge(pct));
          });
        });

        return Promise.all(promises);
      })
      .catch(function (err) {
        if (window.console && console.warn) {
          console.warn('[syba-cart-perliter-badge] render failed:', err);
        }
      });
  }

  function injectOurBadge(domItem, text) {
    domItem.setAttribute('data-syba-mix', 'true');
    var existing = domItem.querySelector('.syba-perliter-badge');
    if (existing) {
      if (existing.textContent !== text) existing.textContent = text;
      return;
    }
    var badge = document.createElement('div');
    badge.className = 'syba-perliter-badge';
    badge.textContent = text;
    var priceInfo = domItem.querySelector('.kaching-cart-item__price-info');
    if (priceInfo) priceInfo.appendChild(badge);
  }

  function removeOurBadge(domItem) {
    var existing = domItem.querySelector('.syba-perliter-badge');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    domItem.removeAttribute('data-syba-mix');
  }

  // Debounced trigger — coalesces rapid mutation/event bursts into one render.
  var renderPending = false;
  function scheduleRender() {
    if (renderPending) return;
    renderPending = true;
    setTimeout(function () {
      renderPending = false;
      renderBadges();
    }, 200);
  }

  function init() {
    // Listen for every cart-update event we can — Kaching, Dawn, our offer module.
    var events = [
      'cart:added', 'cart:refresh', 'cart:updated', 'cart:open',
      'kaching:cart-updated', 'kaching:cart-opened', 'kaching:cart-rendered'
    ];
    events.forEach(function (e) {
      document.addEventListener(e, scheduleRender);
    });

    // MutationObserver fallback — fires when Kaching cart drawer becomes active
    // OR when its inner content re-renders (which Kaching does on qty change).
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        // Class change on .kaching-cart (active toggle)
        if (m.type === 'attributes' && m.target.classList
            && m.target.classList.contains('kaching-cart')) {
          scheduleRender();
          return;
        }
        // Children added inside the drawer (Kaching re-render on qty change)
        if (m.type === 'childList' && m.target.closest
            && m.target.closest('.kaching-cart, kaching-cart-drawer')) {
          // Ignore mutations caused by our OWN badge: injectOurBadge added a
          // .syba-perliter-badge (or removeOurBadge removed one) → this observer
          // → scheduleRender → renderBadges (fetch /cart.js + product fetches) →
          // inject/remove badge → … a feedback loop that hammered /cart.js into
          // HTTP 429 and broke the cart. Treat a mutation as "ours" when EVERY
          // added AND removed node is one of our badges (covers both inject and
          // remove paths).
          if (m.target.closest('.syba-perliter-badge')) continue;
          var touched = m.addedNodes.length + m.removedNodes.length;
          if (touched > 0) {
            var onlyOurs = true;
            var k;
            for (k = 0; k < m.addedNodes.length; k++) {
              var an = m.addedNodes[k];
              if (an.nodeType === 1 && !(an.classList && an.classList.contains('syba-perliter-badge'))) { onlyOurs = false; break; }
            }
            if (onlyOurs) {
              for (k = 0; k < m.removedNodes.length; k++) {
                var rn = m.removedNodes[k];
                if (rn.nodeType === 1 && !(rn.classList && rn.classList.contains('syba-perliter-badge'))) { onlyOurs = false; break; }
              }
            }
            if (onlyOurs) continue; // only our badge changed → not a real cart change
          }
          scheduleRender();
          return;
        }
      }
    });
    observer.observe(document.body, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['class']
    });

    // If cart already open at load (e.g. page reload with drawer open), render now.
    if (document.querySelector('.kaching-cart.active, .kaching-cart[class*="active"]')) {
      scheduleRender();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API for manual trigger / testing.
  return { render: renderBadges, schedule: scheduleRender };
})();
